// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import { t } from 'i18next';
import { call, put, select, take, takeEvery, delay, race } from 'redux-saga/effects';
import { Api } from '../../../../services/config/Api';
import { POLLING_START_DELAY } from '../../../../services/config/FunctionalConstants';
import { DatasetsUtils } from '../../../../utils';
import { DATASET_ACTIONS_KEY, DATASET_PERMISSIONS_MAPPING } from '../../../commons/DatasetConstants';
import { DATASTORE_ACTIONS } from '../../../commons/DatastoreConstants';
import { WORKSPACE_ACTIONS_KEY } from '../../../commons/WorkspaceConstants';
import { dispatchSetApplicationErrorMessage } from '../../../dispatchers/app/ApplicationDispatcher';

const DATASTORE_POLLING_DELAY = 5000;

const getWorkspaceId = (state) => state.workspace.current?.data?.id;
const getOrganizationId = (state) => state.organization.current?.data?.id;
const getDataset = (state, datasetId) => state.dataset.list.data.find((dataset) => dataset.id === datasetId);
const getUserEmail = (state) => state.auth.userEmail;
const getValidationState = (state) => state.datastore.validationState;

function forgeStopPollingAction(runnerId) {
  const actionName = `${DATASTORE_ACTIONS.STOP_RUNNER_STATUS_POLLING}_${runnerId}`;
  return { type: actionName, data: { runnerId } };
}

function* fetchDatasetCreatedByPushRunner(action) {
  const organizationId = yield select(getOrganizationId);
  const workspaceId = yield select(getWorkspaceId);
  const runnerId = action.runnerId;

  const { data: updatedRunner } = yield call(Api.Runners.getRunner, organizationId, workspaceId, runnerId);

  const idOfDatasetToFetch = updatedRunner?.parametersValues.find(
    (param) => param.parameterId === 'last_confirmed_dataset_id'
  )?.value;
  if (idOfDatasetToFetch === undefined) return;

  const datasetFromCache = yield select(getDataset, idOfDatasetToFetch);
  if (datasetFromCache !== undefined) return;

  try {
    const { data: dataset } = yield call(Api.Datasets.findDatasetById, organizationId, idOfDatasetToFetch);

    const userEmail = yield select(getUserEmail);
    DatasetsUtils.patchDatasetWithCurrentUserPermissions(dataset, userEmail, DATASET_PERMISSIONS_MAPPING);

    yield put({ type: DATASET_ACTIONS_KEY.ADD_DATASET, ...dataset });
    yield put({ type: WORKSPACE_ACTIONS_KEY.LINK_TO_DATASET, datasetId: dataset.id, workspaceId });
  } catch (error) {
    if (error.status !== 404) console.error(error); // Ignore silently when dataset has been manually deleted
  }
}

export function* pollRunnerState(action) {
  const organizationId = yield select(getOrganizationId);
  const workspaceId = yield select(getWorkspaceId);

  const { datasetId, runnerType, runnerId, runId } = action;

  // Polling start is delayed to avoid an erroneous Unknown state due to the fact that in first seconds of the run,
  // the AKS container isn't created yet and runner.state passes to Unknown for a few seconds before returning to
  // Running again.
  // For more details, see https://cosmo-tech.atlassian.net/browse/SDCOSMO-1768
  yield delay(POLLING_START_DELAY);
  // Loop until the runner state is FAILED, SUCCESS or UNKNOWN
  while (true) {
    try {
      const { data } = yield call(Api.RunnerRuns.getRun, organizationId, workspaceId, runnerId, runId);
      const lastRunState = data?.state;

      if (['Failed', 'Successful', 'Unknown'].includes(lastRunState)) {
        yield put({
          type: DATASET_ACTIONS_KEY.UPDATE_DATASTORE_ASSOCIATED_RUNNERS,
          datasetId,
          associatedRunnersPatch: {
            [runnerType]: { id: runnerId, lastRunId: runId, lastRunDate: data.createdAt, state: lastRunState },
          },
        });

        if (runnerType === 'push') yield call(fetchDatasetCreatedByPushRunner, { runnerId });
        else {
          const validation = yield select(getValidationState);
          if (validation.status === 'draft')
            yield put({ type: DATASTORE_ACTIONS.TRIGGER_SAGA_FETCH_ALL_WORKSPACE_FILES });

          yield put({ type: DATASTORE_ACTIONS.RESET_VALIDATION_STATE });
        }
        yield put(forgeStopPollingAction(runnerId));
      }

      yield delay(DATASTORE_POLLING_DELAY); // Wait before retrying
    } catch (error) {
      console.error(error);
      yield put(
        dispatchSetApplicationErrorMessage(
          error,
          t('commoncomponents.banner.run', 'A problem occurred during the run.')
        )
      );
      yield put(forgeStopPollingAction(action.runnerId));
    }
  }
}

function* startPolling(action) {
  const stopActionName = `${DATASTORE_ACTIONS.STOP_RUNNER_STATUS_POLLING}_${action.runnerId}`;
  yield put(forgeStopPollingAction(action.runnerId)); // Prevent double polling on same runner

  yield race([
    call(pollRunnerState, action),
    take([stopActionName, DATASTORE_ACTIONS.STOP_ALL_RUNNER_STATUS_POLLINGS]),
  ]);
}

function* pollDatastoreRunnerStatusSaga() {
  yield takeEvery(DATASTORE_ACTIONS.START_RUNNER_STATUS_POLLING, startPolling);
}

export default pollDatastoreRunnerStatusSaga;
